import React, { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { type Campaign } from 'Store/entities/activity/activity.type';
import { t } from 'i18next';

import SSection from 'Components/structural/SSection/SSection';
import SCounter from 'Components/structural/SCounter/SCounter';
import STextIndicator, { IndicatorStatus, OddRangeValues } from 'Components/structural/STextIndicator/STextIndicator';
import SDailySessionContainer from 'Components/structural/SDailySessionContainer/SDailySessionContainer';
import { CampaignFormValues } from 'Pages/Activity/Campaign/saveCampaign.hook';
import { inferSessionStartDate } from 'Pages/Activity/Campaign/dates';

import styles from '../ContentDays.style';

type RandomConfigurationSectionProps = {
  campaignStatus: Campaign['status'];
};

export const RandomConfigurationSection = ({ campaignStatus }: RandomConfigurationSectionProps) => {
  const [indicatorStatus, setIndicatorStatus] = useState<IndicatorStatus>('evenly');
  const [oddRange, setOddRange] = useState<OddRangeValues>({ from: 0, to: 0 });

  const { watch, setValue } = useFormContext<CampaignFormValues>();

  const localSessionCount = useMemo(() => watch('nbSessions') || 0, [watch('nbSessions')]);

  const contentsCount = watch('contents')?.length || 0;
  const minCount = localSessionCount <= 1;

  const contentSelectionLocked = campaignStatus === 'live';

  const processDataForTextIndicator = useMemo(() => {
    const contentsPerSession = contentsCount / localSessionCount;

    if (contentsCount === 0) {
      setIndicatorStatus('evenly');
      return null;
    }

    if (localSessionCount >= 1 && Number.isInteger(contentsPerSession)) {
      setIndicatorStatus('total');
      return contentsPerSession;
    }

    if (localSessionCount < contentsCount && localSessionCount >= 1 && !Number.isInteger(contentsPerSession)) {
      const from = Math.floor(contentsPerSession);
      const to = Math.ceil(contentsPerSession);

      setOddRange({ from, to });
      setIndicatorStatus('odd');
      return null;
    }

    setIndicatorStatus('warning');
    return null;
  }, [contentsCount, localSessionCount]);

  return (
    <SSection style={styles.sectionContainer} containerStyle={styles.sectionContainerInside}>
      <div style={styles.titleContainer}>
        <p style={styles.title}>{t('activities:scheduling.daily_session_number', { number: localSessionCount })}</p>
      </div>
      <div style={styles.counterContainer}>
        {!contentSelectionLocked && (
          <>
            <p style={styles.counterTitle}>{t('activities:scheduling.days_count')}</p>
            <SCounter
              tooltip={{
                tooltipPosition: 'top',
                tooltipContent: t('activities:scheduling.days_count_tooltip'),
                tooltipMaxWidth: 218,
                tooltipEnabled: minCount,
              }}
              minCount={1}
              style={styles.counter}
              count={localSessionCount}
              disabled={minCount || contentSelectionLocked}
              onChange={(value) => {
                setValue('nbSessions', value);
              }}
            />
          </>
        )}
        <STextIndicator status={indicatorStatus} total={processDataForTextIndicator} oddRange={oddRange} />
      </div>
      <>
        {Array(localSessionCount)
          .fill('')
          .map((_, index) => {
            const startDate = inferSessionStartDate(watch, index + 1);

            const modifier = index < contentsCount % localSessionCount ? 1 : 0;
            const contentCount = Math.floor(contentsCount / localSessionCount) + modifier;

            return (
              <SDailySessionContainer
                key={`random-${index}`}
                variant="random"
                sessionNumber={index + 1}
                style={styles.dailySessionContainer}
                contentCount={contentCount}
                startDate={startDate}
                locked={contentSelectionLocked}
                disabledAdd={contentSelectionLocked}
                disabledDelete={contentSelectionLocked}
              />
            );
          })}
      </>
    </SSection>
  );
};
